import { useParams, Link, Navigate } from 'react-router-dom';
import { getArticleBySlug } from '../data/articles';
import { useEffect } from 'react';

function ArticleDetail() {
  const { slug } = useParams();
  const article = getArticleBySlug(slug);

  // Update document title and meta tags for SEO
  useEffect(() => {
    if (article) {
      document.title = `${article.title} | Fatak App News`;
      
      // Update meta description
      let metaDescription = document.querySelector('meta[name="description"]');
      if (!metaDescription) {
        metaDescription = document.createElement('meta');
        metaDescription.setAttribute('name', 'description');
        document.head.appendChild(metaDescription);
      }
      metaDescription.setAttribute('content', article.excerpt);

      // Update Open Graph tags
      const updateMetaTag = (property, content) => {
        let meta = document.querySelector(`meta[property="${property}"]`);
        if (!meta) {
          meta = document.createElement('meta');
          meta.setAttribute('property', property);
          document.head.appendChild(meta);
        }
        meta.setAttribute('content', content);
      };

      updateMetaTag('og:title', article.title);
      updateMetaTag('og:description', article.excerpt);
      updateMetaTag('og:image', `https://fatak.app${article.image}`);
      updateMetaTag('og:url', `https://fatak.app/news/${article.slug}`);
      updateMetaTag('og:type', 'article');
      updateMetaTag('article:published_time', article.date);
      updateMetaTag('article:author', article.author);

      // Update Twitter tags
      updateMetaTag('twitter:title', article.title);
      updateMetaTag('twitter:description', article.excerpt);
      updateMetaTag('twitter:image', `https://fatak.app${article.image}`);

      // Add structured data for article
      const structuredData = {
        "@context": "https://schema.org",
        "@type": "NewsArticle",
        "headline": article.title,
        "description": article.excerpt,
        "image": `https://fatak.app${article.image}`,
        "datePublished": article.date,
        "dateModified": article.date,
        "author": {
          "@type": "Organization",
          "name": article.author
        },
        "publisher": {
          "@type": "Organization",
          "name": "Fatak App",
          "logo": {
            "@type": "ImageObject",
            "url": "https://fatak.app/icon.png"
          }
        },
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": `https://fatak.app/news/${article.slug}`
        }
      };

      // Remove existing structured data script if any
      const existingScript = document.querySelector('script[type="application/ld+json"][data-article]');
      if (existingScript) {
        existingScript.remove();
      }

      // Add new structured data script
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-article', 'true');
      script.text = JSON.stringify(structuredData);
      document.head.appendChild(script);
    }
  }, [article]);

  if (!article) {
    return <Navigate to="/news" replace />;
  }

  return (
    <div className="min-h-screen bg-cover bg-center bg-no-repeat bg-scroll" style={{backgroundImage: "url('/background.png')"}}>
      <div className="container mx-auto px-3 sm:px-4 py-4 sm:py-8">
        {/* Header */}
        <div className="text-center mb-6 sm:mb-8 mt-4 sm:mt-8">
          <Link to="/" className="inline-block mb-3 sm:mb-4">
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-orange-500 hover:text-orange-600 transition-colors">
              Fatak App
            </h1>
          </Link>
          <Link
            to="/news"
            className="inline-block text-sm sm:text-base text-gray-600 hover:text-orange-600 transition-colors mb-4 px-2"
          >
            ← Back to News
          </Link>
        </div>

        {/* Article Content */}
        <div className="max-w-4xl mx-auto mb-12 sm:mb-16">
          <article className="bg-white/95 backdrop-blur-sm rounded-xl sm:rounded-2xl shadow-2xl p-4 sm:p-6 md:p-8 lg:p-12 xl:p-16">
            {/* Article Header */}
            <div className="mb-6 sm:mb-8 pb-4 sm:pb-6 border-b-2 border-orange-200">
              <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                <span className="px-3 sm:px-4 py-1 bg-orange-500 text-white text-xs sm:text-sm font-bold rounded-full whitespace-nowrap">
                  {article.category}
                </span>
                <span className="text-xs sm:text-sm text-gray-500 hidden sm:inline">{article.location}</span>
                <span className="text-xs sm:text-sm text-gray-500 hidden sm:inline">•</span>
                <span className="text-xs sm:text-sm text-gray-500">
                  {new Date(article.date).toLocaleDateString('en-US', { 
                    year: 'numeric', 
                    month: 'short', 
                    day: 'numeric' 
                  })}
                </span>
                <span className="text-xs sm:text-sm text-gray-500 hidden sm:inline">•</span>
                <span className="text-xs sm:text-sm text-gray-500 hidden sm:inline">By {article.author}</span>
                <span className="text-xs text-gray-500 sm:hidden w-full mt-1">{article.location} • By {article.author}</span>
              </div>
              <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-bold text-gray-900 leading-tight">
                {article.title}
              </h1>
            </div>

            {/* Article Body */}
            <div 
              className="prose prose-sm sm:prose-base md:prose-lg max-w-none prose-headings:font-bold prose-p:text-gray-700 prose-p:leading-relaxed prose-ul:text-gray-700 prose-li:text-gray-700"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            {/* Share Section */}
            <div className="mt-8 sm:mt-12 pt-6 sm:pt-8 border-t-2 border-gray-200">
              <p className="text-xs sm:text-sm font-semibold text-gray-700 mb-3 sm:mb-4">Share this article:</p>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                <a
                  href={`https://www.facebook.com/sharer/sharer.php?u=https://fatak.app/news/${article.slug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2.5 bg-blue-600 text-white text-sm sm:text-base rounded-lg hover:bg-blue-700 transition-colors text-center"
                >
                  Facebook
                </a>
                <a
                  href={`https://twitter.com/intent/tweet?url=https://fatak.app/news/${article.slug}&text=${encodeURIComponent(article.title)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2.5 bg-blue-400 text-white text-sm sm:text-base rounded-lg hover:bg-blue-500 transition-colors text-center"
                >
                  Twitter
                </a>
                <a
                  href={`https://wa.me/?text=${encodeURIComponent(article.title + ' - https://fatak.app/news/' + article.slug)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2.5 bg-green-500 text-white text-sm sm:text-base rounded-lg hover:bg-green-600 transition-colors text-center"
                >
                  WhatsApp
                </a>
              </div>
            </div>
          </article>
        </div>
      </div>
    </div>
  );
}

export default ArticleDetail;

